import React, { Component } from "react";
import { connect } from "react-redux";
import {
  Spin,
  Form,
  Row,
  Col,
  Input,
  Switch,
  message,
  Divider,
  Alert,
} from "antd";
import { MailOutlined } from "@ant-design/icons";
import {
  FormItem,
  InputItem,
  ErrorItem,
  ButtonItem,
} from "../../../../Components/Items";
import SegmentedItem from "../../../../Components/SegmentedItem";
import TabbedDynamicTable from "../../../../Components/TabbedDynamicTable";
import DynamicCardForm from "../../../../Components/DynamicCardForm";
import axios from "axios";

const { TextArea } = Input;

// Clave del sub-objeto de estado según número + método de entrega.
const claveEstado = (estado, sucursal) =>
  `estado${estado}${sucursal ? "Sucursal" : ""}`;

class CorreosConfiguracion extends Component {
  formRef = React.createRef();
  previewTimer = null;

  state = {
    loading: false,
    formConCambios: false,
    tabActivo: "pedidoRecibido",
    // Selectores de vista previa
    tipoPagoPreview: "card", // card | bank_transfer
    sucursalPreview: false,
    estadoSel: 2, // estado en edición (2-5)
    // HTML de las vistas previas por tipo
    previews: {},
    previewLoading: false,
  };

  componentDidMount() {
    this.consultarConfiguracion();
  }

  componentWillUnmount() {
    if (this.previewTimer) clearTimeout(this.previewTimer);
  }

  consultarConfiguracion = () => {
    this.setState({ loading: true });
    axios
      .post(
        "configuracionTiendaOnline/obtenerConfiguracionCorreos",
        { empresaId: this.props.empresaId },
        { headers: this.props.headersToken },
      )
      .then((res) => {
        if (res.data.success === 2 && res.data.data) {
          this.formRef.current.setFieldsValue(res.data.data);
          this.actualizarVistaPrevia(this.state.tabActivo);
        }
      })
      .catch((err) => {
        console.error("Error cargando config de correos", err);
        message.error("Error al cargar la configuración de correos");
      })
      .finally(() => {
        this.setState({ loading: false });
        // Aseguramos que la vista previa se genere aunque la carga de config falle
        // (se mostrará con los textos por defecto).
        this.actualizarVistaPrevia(this.state.tabActivo);
      });
  };

  onFinish = async () => {
    this.setState({ loading: true });
    
    const valores = this.formRef.current.getFieldsValue(true);
    const payload = { empresaId: this.props.empresaId, ...valores };
    return axios
      .post(
        "configuracionTiendaOnline/actualizarConfiguracionCorreos",
        payload,
        { headers: this.props.headersToken },
      )
      .then((res) => {
        if (res.data.success === 2) {
          message.success("Se ha guardado la configuración de correos");
          this.setState({ formConCambios: false });
          return true;
        }
        message.error("No se pudo guardar la configuración");
        return false;
      })
      .catch((err) => {
        console.error(err);
        message.error("Error al guardar");
        return false;
      })
      .finally(() => this.setState({ loading: false }));
  };

  onFinishFailed = (e) => ErrorItem(e);

  tieneCambios = () => this.state.formConCambios;

  guardarDesdePadre = () =>
    this.formRef.current
      .validateFields()
      .then((values) => this.onFinish(values))
      .catch(() => false);

  descartarCambios = () => {
    this.setState({ formConCambios: false });
    this.consultarConfiguracion();
  };

  handleValuesChange = () => {
    if (!this.state.formConCambios) this.setState({ formConCambios: true });
    // Refrescar la vista previa con debounce.
    if (this.previewTimer) clearTimeout(this.previewTimer);
    this.previewTimer = setTimeout(
      () => this.actualizarVistaPrevia(this.state.tabActivo),
      500,
    );
  };

  obtenerOverrides = (tipo) => {
    const valores = this.formRef.current?.getFieldsValue() || {};
    if (tipo === "estadoPedido") {
      const key = claveEstado(this.state.estadoSel, this.state.sucursalPreview);
      return valores.estadoPedido?.[key] || {};
    }
    return valores[tipo] || {};
  };

  actualizarVistaPrevia = (tipo) => {
    if (!tipo) return;
    this.setState({ previewLoading: true });
    const body = {
      tipoCorreo: tipo,
      estado: this.state.estadoSel,
      esEntregaSucursal: this.state.sucursalPreview,
      tipoPago: this.state.tipoPagoPreview,
      overrides: this.obtenerOverrides(tipo),
      logoPreview: `${window.location.origin}/logo512.png`,
    };
    axios
      .post("configuracionTiendaOnline/previsualizarCorreo", body, {
        headers: this.props.headersToken,
      })
      .then((res) => {
        if (res.data.success === 2) {
          this.setState((prev) => ({
            previews: { ...prev.previews, [tipo]: res.data.html },
          }));
        }
      })
      .catch((err) => console.error("Error en vista previa", err))
      .finally(() => this.setState({ previewLoading: false }));
  };

  onTabChange = (tabActivo) => {
    this.setState(
      (prev) => {
        const patch = { tabActivo };
        // Las tabs de estado (estado2..estado5) fijan el estado a previsualizar.
        if (tabActivo.startsWith("estado")) {
          const n = parseInt(tabActivo.replace("estado", ""));
          if (!Number.isNaN(n)) {
            patch.estadoSel = n;
            if (n === 4) patch.sucursalPreview = false; // sucursal no tiene tránsito
          }
        }
        return patch;
      },
      () => {
        const tipo = this.state.tabActivo.startsWith("estado")
          ? "estadoPedido"
          : this.state.tabActivo;
        this.actualizarVistaPrevia(tipo);
      },
    );
  };

  // Cambia el método de entrega de la vista previa. En sucursal no existe el
  // estado 4 (En tránsito), así que si estaba seleccionado lo movemos a 3.
  setSucursalPreview = (esSucursal, tipo) => {
    this.setState(
      (prev) => ({
        sucursalPreview: esSucursal,
        estadoSel: esSucursal && prev.estadoSel === 4 ? 3 : prev.estadoSel,
      }),
      () => this.actualizarVistaPrevia(tipo),
    );
  };


  // Panel de vista previa (iframe aislado).
  renderPreview = (tipo) => (
    <div style={{ position: "sticky", top: 12 }}>
      <Divider orientation="left" style={{ marginTop: 0 }}>
        Vista previa
      </Divider>
      <Spin spinning={this.state.previewLoading}>
        <iframe
          title={`preview-${tipo}`}
          srcDoc={this.state.previews[tipo] || ""}
          style={{
            width: "100%",
            height: 560,
            border: "1px solid #e5e7eb",
            borderRadius: 8,
            background: "#f3f4f6",
          }}
        />
      </Spin>
      <ButtonItem
        blanco
        onClick={() => this.actualizarVistaPrevia(tipo)}
        style={{ marginTop: 8 }}
      >
        Actualizar vista previa
      </ButtonItem>
    </div>
  );

  // Una tab por cada correo de estado (Empaquetado, Orden de entrega, etc.).
  // En tránsito (estado 4) no aplica a sucursal, así que se edita solo domicilio.
  renderEstadoTab = (estadoNum, label) => {
    const permiteSucursal = estadoNum !== 4;
    const esSucursal = permiteSucursal ? this.state.sucursalPreview : false;
    const key = claveEstado(estadoNum, esSucursal);
    const sufijo = permiteSucursal ? (esSucursal ? " — sucursal" : " — domicilio") : "";

    return {
      key: `estado${estadoNum}`,
      label,
      customContent: (
        <Row gutter={24}>
          <Col xs={24} lg={13}>
            {permiteSucursal && (
              <>
                <DynamicCardForm title="Método de entrega" style={{ maxWidth: "99%" }}>
                  <FormItem label="Editar / previsualizar para" margin>
                    <SegmentedItem
                      options={[
                        { label: "Domicilio", value: "dom" },
                        { label: "Sucursal", value: "suc" },
                      ]}
                      value={esSucursal ? "suc" : "dom"}
                      onChange={(v) =>
                        this.setSucursalPreview(v === "suc", "estadoPedido")
                      }
                    />
                  </FormItem>
                </DynamicCardForm>
                <div style={{ marginTop: 20 }} />
              </>
            )}
            <DynamicCardForm title={`Correo de "${label}"${sufijo}`} style={{ maxWidth: "99%" }}>
              <FormItem
                label="Enviar este correo por defecto"
                name={["estadoPedido", key, "enviarPorDefecto"]}
                valuePropName="checked"
                margin
                tooltip="Si lo apagas, el interruptor de '¿enviar correo?' del panel aparecerá apagado por defecto en esta transición."
              >
                <Switch />
              </FormItem>
              <FormItem label="Asunto" name={["estadoPedido", key, "asunto"]} margin>
                <InputItem placeholder="Asunto del correo (sin el folio)" />
              </FormItem>
              <FormItem label="Encabezado" name={["estadoPedido", key, "encabezado"]} margin tooltip="Usa {nombreCliente} para incluir el nombre.">
                <InputItem placeholder="Encabezado del correo" />
              </FormItem>
              <FormItem label="Mensaje" name={["estadoPedido", key, "mensaje"]} margin>
                <TextArea rows={3} placeholder="Mensaje del correo" />
              </FormItem>
            </DynamicCardForm>
          </Col>
          <Col xs={24} lg={11}>{this.renderPreview("estadoPedido")}</Col>
        </Row>
      ),
    };
  };

  render() {
    const { loading } = this.state;

    const tabItems = [
      {
        key: "pedidoRecibido",
        label: "Pedido recibido",
        customContent: (
          <Row gutter={24}>
            <Col xs={24} lg={13}>
              <Alert
                type="warning"
                showIcon
                message="Correo obligatorio"
                description="Este correo siempre se envía cuando se crea un pedido; no se puede desactivar."
                style={{ marginBottom: 16 }}
              />
              <DynamicCardForm title="Encabezado y mensaje" style={{ maxWidth: "99%" }}>
                <FormItem label="Encabezado" name={["pedidoRecibido", "encabezado"]} margin tooltip="Usa {nombreCliente} para el nombre del cliente.">
                  <InputItem placeholder="¡Hola, {nombreCliente}!" />
                </FormItem>
                <FormItem label="Mensaje principal" name={["pedidoRecibido", "mensajePrincipal"]} margin tooltip="Usa {folioVenta} para el folio del pedido.">
                  <TextArea rows={3} placeholder="Hemos registrado tu pedido #{folioVenta} con éxito..." />
                </FormItem>
              </DynamicCardForm>
              <div style={{ marginTop: 20 }} />
              <DynamicCardForm title="Leyendas por método de pago" style={{ maxWidth: "99%" }}>
                <FormItem label="Leyenda — Tarjeta de crédito/débito" name={["pedidoRecibido", "leyendaTarjeta"]} margin>
                  <TextArea rows={2} placeholder="Estamos procesando la validación de tu pago. En breve te confirmaremos." />
                </FormItem>
                <FormItem label="Leyenda — Transferencia (SPEI)" name={["pedidoRecibido", "leyendaTransferencia"]} margin tooltip="Debajo de esta leyenda se muestra la CLABE y la página de pago (iframe) que devuelve Mattilda.">
                  <TextArea rows={2} placeholder="Realiza tu transferencia (SPEI) con los siguientes datos:" />
                </FormItem>
                <FormItem label="Leyenda — Pago en efectivo (OXXO)" name={["pedidoRecibido", "leyendaOxxo"]} margin tooltip="Debajo de esta leyenda se muestra la página de pago (iframe) con el código de barras y referencia.">
                  <TextArea rows={2} placeholder="Acude a tu OXXO más cercano y realiza tu pago con los siguientes datos:" />
                </FormItem>
                <FormItem label="Previsualizar método de pago" margin>
                  <SegmentedItem
                    options={[
                      { label: "Tarjeta", value: "card" },
                      { label: "Transferencia (SPEI)", value: "bank_transfer" },
                      { label: "OXXO", value: "cash" },
                    ]}
                    value={this.state.tipoPagoPreview}
                    onChange={(v) =>
                      this.setState({ tipoPagoPreview: v }, () =>
                        this.actualizarVistaPrevia("pedidoRecibido"),
                      )
                    }
                  />
                </FormItem>
              </DynamicCardForm>
            </Col>
            <Col xs={24} lg={11}>{this.renderPreview("pedidoRecibido")}</Col>
          </Row>
        ),
      },
      ...(this.props.escuela
        ? [
            {
              key: "pedidoRecibidoTransferenciaFija",
              label: "Pedido recibido — Transferencia fija",
              customContent: (
                <Row gutter={24}>
                  <Col xs={24} lg={13}>
                    <Alert
                      type="info"
                      showIcon
                      message="Variante para transferencia fija (cuenta de la escuela)"
                      description="Se usa cuando la escuela cobra por transferencia a su cuenta bancaria fija. Los campos vacíos usan el texto del correo 'Pedido recibido' y luego el texto por defecto. Las instrucciones configuradas en el método de pago de la escuela tienen prioridad sobre la leyenda."
                      style={{ marginBottom: 16 }}
                    />
                    <DynamicCardForm title="Encabezado y mensaje" style={{ maxWidth: "99%" }}>
                      <FormItem label="Encabezado" name={["pedidoRecibidoTransferenciaFija", "encabezado"]} margin tooltip="Usa {nombreCliente} para el nombre del cliente.">
                        <InputItem placeholder="¡Hola, {nombreCliente}!" />
                      </FormItem>
                      <FormItem label="Mensaje principal" name={["pedidoRecibidoTransferenciaFija", "mensajePrincipal"]} margin tooltip="Usa {folioVenta} para el folio del pedido.">
                        <TextArea rows={3} placeholder="Hemos registrado tu pedido #{folioVenta} con éxito..." />
                      </FormItem>
                      <FormItem label="Leyenda de pago" name={["pedidoRecibidoTransferenciaFija", "leyenda"]} margin tooltip="Se muestra arriba de las cuentas y la referencia de pago (CLABE y/o cuenta de depósito).">
                        <TextArea rows={2} placeholder="Elige transferencia o depósito en banco/cajero y usa la referencia para identificar tu pago:" />
                      </FormItem>
                    </DynamicCardForm>
                  </Col>
                  <Col xs={24} lg={11}>{this.renderPreview("pedidoRecibidoTransferenciaFija")}</Col>
                </Row>
              ),
            },
          ]
        : []),
      {
        key: "pagoConfirmado",
        label: "Pago confirmado",
        customContent: (
          <Row gutter={24}>
            <Col xs={24} lg={13}>
              <Alert
                type="warning"
                showIcon
                message="Correo obligatorio"
                description="Este correo siempre se envía cuando se confirma el pago; no se puede desactivar."
                style={{ marginBottom: 16 }}
              />
              <DynamicCardForm title="Encabezado y mensaje" style={{ maxWidth: "99%" }}>
                <FormItem label="Título" name={["pagoConfirmado", "titulo"]} margin tooltip="Usa {nombreCliente} para el nombre del cliente.">
                  <InputItem placeholder="¡Buenas noticias, {nombreCliente}!" />
                </FormItem>
                <FormItem label="Mensaje" name={["pagoConfirmado", "mensaje"]} margin>
                  <TextArea rows={3} placeholder="Te confirmamos que hemos recibido tu pago con éxito..." />
                </FormItem>
              </DynamicCardForm>
              <div style={{ marginTop: 20 }} />
              <DynamicCardForm title="Cierre según método de entrega" style={{ maxWidth: "99%" }}>
                <FormItem label="Cierre — Envío a domicilio" name={["pagoConfirmado", "cierreDomicilio"]} margin>
                  <TextArea rows={2} placeholder="Te enviaremos otro correo en cuanto tu paquete vaya en camino." />
                </FormItem>
                <FormItem label="Cierre — Entrega en sucursal" name={["pagoConfirmado", "cierreSucursal"]} margin>
                  <TextArea rows={2} placeholder="Estamos preparando tu pedido. Te avisaremos cuando esté listo para recoger." />
                </FormItem>
                <FormItem label="Previsualizar entrega" margin>
                  <SegmentedItem
                    options={[
                      { label: "Domicilio", value: "dom" },
                      { label: "Sucursal", value: "suc" },
                    ]}
                    value={this.state.sucursalPreview ? "suc" : "dom"}
                    onChange={(v) =>
                      this.setSucursalPreview(v === "suc", "pagoConfirmado")
                    }
                  />
                </FormItem>
              </DynamicCardForm>
            </Col>
            <Col xs={24} lg={11}>{this.renderPreview("pagoConfirmado")}</Col>
          </Row>
        ),
      },
      ...(this.props.escuela
        ? [
            {
              key: "libroVirtual",
              label: "Libro Virtual",
              customContent: (
                <Row gutter={24}>
                  <Col xs={24} lg={13}>
                    <Alert
                      type="info"
                      showIcon
                      message="Correo aparte del de 'Pago confirmado'"
                      description="Se envía únicamente cuando la venta incluye insumos marcados como 'Libro Virtual', junto con el/los código(s) asignados."
                      style={{ marginBottom: 16 }}
                    />
                    <DynamicCardForm title="Encabezado y mensaje" style={{ maxWidth: "99%" }}>
                      <FormItem
                        label="Enviar este correo por defecto"
                        name={["libroVirtual", "enviarPorDefecto"]}
                        valuePropName="checked"
                        margin
                      >
                        <Switch />
                      </FormItem>
                      <FormItem label="Título" name={["libroVirtual", "titulo"]} margin tooltip="Usa {nombreCliente} para el nombre del cliente.">
                        <InputItem placeholder="¡Tus códigos ya están listos, {nombreCliente}!" />
                      </FormItem>
                      <FormItem label="Mensaje" name={["libroVirtual", "mensaje"]} margin>
                        <TextArea rows={3} placeholder="Junto con la confirmación de tu pago, te compartimos el/los código(s) asignados a tu compra." />
                      </FormItem>
                      <FormItem label="Cierre" name={["libroVirtual", "cierre"]} margin>
                        <TextArea rows={2} placeholder="Guarda este correo, lo necesitarás para acceder a tu libro virtual." />
                      </FormItem>
                    </DynamicCardForm>
                  </Col>
                  <Col xs={24} lg={11}>{this.renderPreview("libroVirtual")}</Col>
                </Row>
              ),
            },
          ]
        : []),
      this.renderEstadoTab(2, "Empaquetado"),
      this.renderEstadoTab(3, "Orden de entrega"),
      this.renderEstadoTab(4, "En tránsito"),
      this.renderEstadoTab(5, "Entregado"),
    ];

    return (
      <Spin spinning={loading}>
        <Form
          name="correosConfiguracion"
          layout="vertical"
          onFinish={this.onFinish}
          onFinishFailed={this.onFinishFailed}
          onValuesChange={this.handleValuesChange}
          ref={this.formRef}
        >
          <Alert
            type="info"
            showIcon
            icon={<MailOutlined />}
            message="Personaliza los correos que recibe el cliente"
            description="Deja un campo vacío para usar el texto por defecto. Puedes usar {nombreCliente} y {folioVenta} como variables."
            style={{ marginBottom: 16 }}
          />
          <TabbedDynamicTable
            tabs={tabItems.map((t) => ({ ...t, forceRender: true }))}
            defaultActiveKey={this.state.tabActivo}
            onTabChange={this.onTabChange}
          />
          <Divider style={{ margin: "20px 0" }} />
          <ButtonItem
            type="primary"
            htmlType="submit"
            style={{ display: "block", margin: "0 0 0 auto" }}
          >
            Guardar Configuración
          </ButtonItem>
        </Form>
      </Spin>
    );
  }
}

const mapStateToProps = (state) => ({
  headersToken: state.user.headersToken,
  empresaId: state.empresa.id,
  escuela: state.empresa.escuela,
});

export default connect(mapStateToProps, null, null, { forwardRef: true })(
  CorreosConfiguracion,
);
